import {
  DownOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { ReactComponent as CalendarIcon } from '../../../Assets/sib/calendar.svg';
import { DotLottiePlayer } from "@dotlottie/react-player";
import { Empty, Typography } from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { AppContext } from "../../../App";
import { ReactComponent as BackButton } from "../../../Assets/sib/back_button.svg";
import { ReactComponent as NoTxn } from "../../../Assets/noTxn.svg";
import { ReactComponent as FaqIcon } from "../../../Assets/sib/FaqIcon.svg";
import { ReactComponent as ArrowRight } from "../../../Assets/sib/arrow-right.svg";
import { ReactComponent as ArrowLeft } from "../../../Assets/sib/arrow-left.svg";
import { getTransactions } from "../../../Services/PFMClient/DashboardService";
import { getLottiePath } from "../../../Utils/assetUtils";
import { EVENTCODES, pushEventToParent } from "../../../Utils/eventUtils";
import { ROUTES } from "../../../Utils/routes";
import Transaction from "../Common/Transaction";

import styles from "./index.module.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface CalendarViewProps {
  onBack: () => void;
  onOpenFAQ?: () => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ onBack, onOpenFAQ }) => {
  const { data, statusData, analyticsStatus, isAmountVisible, currentRoute, setCurrentRoute, setPreviousRoute }: any =
    useContext(AppContext);

  const today = dayjs();
  const [selectedDate, setSelectedDate] = useState(today);
  const [isMonthView, setIsMonthView] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;
  const dateSelectorRef = useRef<HTMLDivElement>(null);

  const dateRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Active accounts, shared by the transaction fetch (account IDs) and the
  // calendar's lower date bound (earliest transactionStartDate across them).
  const activeAccountProfiles = useMemo(() => {
    const accountDetails =
      data.current?.ACCOUNT_DETAILS?.accountProfileList || [];

    const activeLinkRefs = new Set(
      (statusData.current?.accountDetails || [])
        .filter((detail: any) => detail.status === "ACTIVE")
        .map((detail: any) => detail.linkRefNumber),
    );

    return accountDetails.filter((acc: any) =>
      activeLinkRefs.has(acc.account.linkedAccRef),
    );
  }, [data.current?.ACCOUNT_DETAILS, statusData.current?.accountDetails]);

  // Get account IDs for fetching transactions
  const accountIds = useMemo(
    () => activeAccountProfiles.map((acc: any) => acc.account.id),
    [activeAccountProfiles],
  );

  // Stable key so the fetch effect only re-runs when the actual set of
  // account IDs changes, not merely when statusData/data refs are replaced.
  const accountIdsKey = accountIds.join(",");

  // Earliest date any active account has transaction data for - the
  // calendar shouldn't let the user navigate further back than this.
  const earliestTransactionDate = useMemo(() => {
    const startDates = activeAccountProfiles
      .map((acc: any) => acc.summary?.transactionStartDate)
      .filter(Boolean)
      .map((d: string) => dayjs(d).startOf("day"));

    if (startDates.length === 0) return null;

    return startDates.reduce((earliest: dayjs.Dayjs, d: dayjs.Dayjs) =>
      d.isBefore(earliest) ? d : earliest,
    );
  }, [activeAccountProfiles]);

  const years = useMemo(() => {
    const currentYear = today.year();
    const earliestYear = earliestTransactionDate
      ? earliestTransactionDate.year()
      : currentYear - 1;
    const count = Math.max(1, currentYear - earliestYear + 1);
    return Array.from({ length: count }, (_, i) => currentYear - i);
  }, [earliestTransactionDate]);

  const displayDates = useMemo(() => {
    const dates = [];
    for (let i = -30; i <= 30; i++) {
      dates.push(selectedDate.add(i, "day"));
    }
    return dates;
  }, [selectedDate]);

  const prevMonth = selectedDate.subtract(1, "month");
  const nextMonth = selectedDate.add(1, "month");
  const isPrevMonthDisabled =
    !!earliestTransactionDate &&
    prevMonth.isBefore(earliestTransactionDate, "month");

  const fetchTransactions = async (reset = false, pageOverride?: number) => {
    if (accountIds.length === 0 || analyticsStatus === "PENDING") {
      setLoading(false);
      return;
    }

    setLoading(true);
    const currentPage = reset ? 0 : (pageOverride ?? page);

    try {
      const startDate = isMonthView
        ? selectedDate.startOf("month").format("YYYY-MM-DD")
        : selectedDate.format("YYYY-MM-DD");
      const endDate = isMonthView
        ? selectedDate.endOf("month").format("YYYY-MM-DD")
        : selectedDate.add(1, "day").format("YYYY-MM-DD");

      const response = await getTransactions(
        {
          accountIdList: accountIds,
          startDate,
          endDate,
        },
        { page: currentPage, pageSize },
      );

      const data = response?.data;
      const newTransactions = data?.elements || [];

      if (data?.totalPages <= currentPage + 1) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      if (reset) {
        setTransactions(newTransactions);
        setPage(0);
      } else {
        setTransactions((prev) => [...prev, ...newTransactions]);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHasMore(true);
    fetchTransactions(true);

    const key = selectedDate.format("YYYY-MM-DD");
    const el = dateRefs.current[key];

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedDate, isMonthView, accountIdsKey, analyticsStatus]);

  // Handle date selection
  const handleDateSelect = (date: dayjs.Dayjs) => {
    pushEventToParent({ eventCode: EVENTCODES.CALENDAR_DATE_SELECTED });
    setSelectedDate(date);
  };

  // Handle month navigation
  const handlePrevMonth = () => {
    const prev = selectedDate.subtract(1, "month").startOf("month");
    if (
      !earliestTransactionDate ||
      !prev.isBefore(earliestTransactionDate, "month")
    ) {
      setSelectedDate(prev);
    }
  };

  const handleNextMonth = () => {
    const next = selectedDate.add(1, "month").startOf("month");
    if (next.isBefore(today) || next.isSame(today, "month")) {
      setSelectedDate(next);
    }
  };

  const handleYearSelect = (year: number) => {
    const newDate = selectedDate.year(year);
    if (
      earliestTransactionDate &&
      newDate.isBefore(earliestTransactionDate, "day")
    ) {
      setSelectedDate(earliestTransactionDate);
    } else if (newDate.isBefore(today) || newDate.isSame(today, "day")) {
      setSelectedDate(newDate);
    } else {
      setSelectedDate(today);
    }
    setShowYearDropdown(false);
  };

  // Handle view more
  const handleViewMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchTransactions(false, nextPage);
  };

  const getDateLabel = () => {
    if (isMonthView) {
      return selectedDate.format("MMMM YYYY");
    }
    return selectedDate.format("dddd D MMMM");
  };

  const handleBack = () => {
    pushEventToParent({ eventCode: EVENTCODES.BACK_BUTTON_CALENDAR });
    onBack();
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <BackButton onClick={handleBack} className={styles.backButton} />
          <Typography.Text className={styles.headerTitle}>
            Calendar View
          </Typography.Text>
        </div>
        <FaqIcon
          className={styles.headerIcon}
          onClick={() => {
            if (onOpenFAQ) {
              onOpenFAQ();
            } else {
              setPreviousRoute(currentRoute);
              setCurrentRoute(ROUTES.SIB_FAQ);
            }
          }}
        />
      </div>

      {/* Calendar Section */}
      <div className={styles.calendarSection}>
        {/* Year Selector */}
        <div className={styles.yearSelectorContainer}>
          <div
            className={styles.yearSelector}
            onClick={() => setShowYearDropdown(!showYearDropdown)}
          >
            <CalendarIcon className={styles.yearIcon} />
            <Typography.Text className={styles.yearText}>
              {selectedDate.year()}
            </Typography.Text>
            <DownOutlined className={styles.yearDropdownIcon} />
          </div>

          {showYearDropdown && (
            <div className={styles.yearDropdown}>
              {years.map((year) => (
                <div
                  key={year}
                  className={`${styles.yearOption} ${
                    selectedDate.year() === year
                      ? styles.yearOptionSelected
                      : ""
                  }`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Month Navigation */}
        <div className={styles.monthNavigation}>
          <div className={styles.navArrowContainer} onClick={handlePrevMonth}>
            <ArrowLeft
              className={`${styles.navArrow} ${isPrevMonthDisabled ? styles.navArrowDisabled : ""}`}
            />
          </div>
          <div className={styles.monthDisplay}>
            <Typography.Text
              className={`${styles.adjacentMonth} ${isPrevMonthDisabled ? styles.adjacentMonthDisabled : ""}`}
              onClick={handlePrevMonth}
            >
              {MONTHS[prevMonth.month()]}
            </Typography.Text>
            <Typography.Text className={styles.currentMonth}>
              {MONTHS[selectedDate.month()]}
            </Typography.Text>
            <Typography.Text
              className={`${styles.adjacentMonth} ${nextMonth.isAfter(today, "month") ? styles.adjacentMonthDisabled : ""}`}
              onClick={handleNextMonth}
            >
              {MONTHS[nextMonth.month()]}
            </Typography.Text>
          </div>
          <div className={styles.navArrowContainer} onClick={handleNextMonth}>
            <ArrowRight
              className={`${styles.navArrow} ${nextMonth.isAfter(today, "month") ? styles.navArrowDisabled : ""}`}
            />
          </div>
        </div>

        {/* Date Selector - Only show when NOT in month view */}
        {!isMonthView && (
          <div className={styles.dateSelector} ref={dateSelectorRef}>
            {displayDates.map((date) => {
              const isSelected = date.isSame(selectedDate, "day");
              const isFuture = date.isAfter(today, "day");
              const isBeforeEarliest =
                !!earliestTransactionDate &&
                date.isBefore(earliestTransactionDate, "day");
              const isDisabled = isFuture || isBeforeEarliest;
              const key = date.format("YYYY-MM-DD");

              return (
                <div
                  key={date.format("YYYY-MM-DD")}
                  ref={(el) => (dateRefs.current[key] = el)}
                  className={`${styles.dateItem} ${isSelected ? styles.dateItemSelected : ""} ${isDisabled ? styles.dateItemFuture : ""}`}
                  onClick={() => !isDisabled && handleDateSelect(date)}
                >
                  <Typography.Text className={styles.dateMonth}>
                    {MONTHS_SHORT[date.month()]}
                  </Typography.Text>
                  <Typography.Text className={styles.dateDay}>
                    {date.date()}
                  </Typography.Text>
                  <Typography.Text className={styles.dateDayName}>
                    {DAY_NAMES[date.day()]}
                  </Typography.Text>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transactions Section */}
      <div className={styles.transactionsSection}>
        <div className={styles.transactionsHeader}>
          <Typography.Text className={styles.dateLabel}>
            {getDateLabel()}
          </Typography.Text>
          <div className={styles.monthViewToggle}>
            <Typography.Text className={styles.monthViewLabel}>
              Month View
            </Typography.Text>
            <button
              className={`${styles.toggleTrack} ${isMonthView ? styles.toggleChecked : ''}`}
              onClick={() => setIsMonthView(!isMonthView)}
              role='switch'
              aria-checked={isMonthView}
            >
              <span className={styles.toggleThumb} />
            </button>
          </div>
        </div>

        <div className={styles.transactionsList}>
          {transactions.length > 0 ? (
            <>
              {transactions.map((transaction: any, index: number) => (
                <Transaction
                  key={transaction.id || index}
                  transaction={transaction}
                  showTransactionDetails={true}
                />
              ))}

              {loading && (
                <div className={styles.loadingContainer}>
                  <DotLottiePlayer
                    speed={2}
                    src={getLottiePath("loading.lottie")}
                    autoplay
                    loop
                    style={{ height: "60px" }}
                  />
                </div>
              )}

              {!loading && hasMore && (
                <div className={styles.viewMoreBtn} onClick={handleViewMore}>
                  <DownOutlined /> View More
                </div>
              )}
            </>
          ) : loading ? (
            <div className={styles.loadingContainer}>
              <DotLottiePlayer
                speed={2}
                src={getLottiePath("loading.lottie")}
                autoplay
                loop
                style={{ height: "100px" }}
              />
            </div>
          ) : (
            <Empty
              className={styles.emptyState}
              image={<NoTxn className={styles.emptyIcon} />}
              description={
                <Typography.Text className={styles.emptyText}>
                  No transactions found
                </Typography.Text>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
